import MissoesCaatingaLogo from "@/assets/missoes-caatinga-logo.png";
import MissoesCaatingaMasonryGallery from "@/components/sections/MissoesCaatingaMasonryGallery";

const MissoesCaatinga = () => {
  return (
    <section className="section-shell">
      <header className="mb-10 grid gap-6 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] md:items-center">
        <div className="space-y-3 max-w-2xl">
          <p className="section-title">Projeto Missões Caatinga</p>
          <h1 className="section-heading">Esperança em meio à seca.</h1>
          <p className="section-subtitle">
            A Caatinga é um dos cenários mais desafiadores do Brasil. Em meio à seca e à escassez, o IESSA caminha com
            igrejas, missionários e voluntários para anunciar Cristo e servir comunidades de forma contínua.
          </p>
        </div>

        <div className="flex items-center justify-center">
          <div className="relative flex items-center justify-center rounded-3xl bg-primary/5 px-6 py-5 shadow-inner md:px-8 md:py-7">
            <img
              src={MissoesCaatingaLogo}
              alt="Logotipo do projeto Missões Caatinga"
              className="max-h-32 w-auto object-contain md:max-h-40"
              loading="lazy"
              width={160}
              height={160}
            />
          </div>
        </div>
      </header>

      <div className="grid gap-8 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] md:items-start">
        <article className="space-y-4">
          <h2 className="text-2xl font-semibold">Um projeto, muitos encontros.</h2>
          <p className="text-sm text-muted-foreground">
            As frentes de Missões Caatinga incluem visitas periódicas, acompanhamento pastoral, evangelização nas
            casas, apoio a crianças e ações emergenciais em tempos de maior necessidade.
          </p>
          <p className="text-sm text-muted-foreground">
            Cada viagem é planejada em oração, com cuidado logístico e respeito à cultura local, sempre em parceria com
            obreiros e igrejas que já atuam na região.
          </p>

          <div className="grid gap-4 md:grid-cols-3">
            <ImpactCard title="Viagens missionárias" subtitle="Equipes que se deslocam regularmente para o sertão." />
            <ImpactCard title="Comunidades alcançadas" subtitle="Povoados visitados e acompanhados ao longo do ano." />
            <ImpactCard title="Ações sociais" subtitle="Cuidado prático com famílias em vulnerabilidade." />
          </div>
        </article>

        <aside className="card-ambient p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">Como apoiar</p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• Orando regularmente pelo sertão e pelas equipes enviadas.</li>
            <li>• Contribuindo financeiramente com ofertas específicas para Missões Caatinga.</li>
            <li>• Envolvendo sua igreja em parcerias e envio de voluntários.</li>
          </ul>
          <p className="mt-4 text-sm text-muted-foreground"></p>
        </aside>
      </div>

      <section className="mt-12 space-y-6">
        <h2 className="text-xl font-semibold">Histórias que florescem no sertão.</h2>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Imagine rodas de conversa sob o céu aberto, cultos simples em salões improvisados, crianças aprendendo sobre
          Jesus entre sorrisos e poeira. Cada encontro é um lembrete de que Deus não esqueceu nenhum vilarejo.
        </p>

        <div className="grid gap-6 md:grid-cols-3">
          <StoryCard
            title="A casa que virou ponto de luz"
            description="Uma família que abriu as portas para os cultos e hoje acolhe vizinhos semanalmente para ler a Bíblia."
          />
          <StoryCard
            title="Água, pão e Palavra"
            description="Ações emergenciais que unem alimento, água e consolo espiritual em tempos de seca mais intensa."
          />
          <StoryCard
            title="Crianças lembradas pelo nome"
            description="Programações que acolhem crianças com carinho, brincadeiras e ensino bíblico contextualizado."
          />
        </div>
      </section>

      <section className="mt-12 space-y-6">
        <header className="space-y-2">
          <h2 className="text-xl font-semibold">Galeria</h2>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Registros visuais que ajudam a contar (em imagens) a beleza e os desafios do sertão.
          </p>
        </header>
        <MissoesCaatingaMasonryGallery />
      </section>
    </section>
  );
};

interface ImpactCardProps {
  title: string;
  subtitle: string;
}

const ImpactCard = ({ title, subtitle }: ImpactCardProps) => (
  <div className="card-ambient p-4">
    <p className="text-sm font-semibold text-foreground">{title}</p>
    <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
  </div>
);

interface StoryCardProps {
  title: string;
  description: string;
}

const StoryCard = ({ title, description }: StoryCardProps) => (
  <article className="card-ambient p-5">
    <h3 className="mb-2 text-sm font-semibold text-foreground">{title}</h3>
    <p className="text-sm text-muted-foreground">{description}</p>
  </article>
);

export default MissoesCaatinga;
